import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { pApi, errMsg, PART_KEY, fmtTime } from "../lib/api";
import { useLang, LangToggle } from "../lib/i18n";
import { ParishMark, Field, inputCls, StatusBadge, Loading } from "../components/common";
import { Section } from "../components/ParticipantForm";
import { IdCardBlock } from "../components/IdCard";

export default function Profile() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [login, setLogin] = useState({ code: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const [corr, setCorr] = useState({ field: "", value: "", reason: "", attendance_id: "" });
  const [pins, setPins] = useState({ current_pin: "", new_pin: "" });
  const [checking, setChecking] = useState(!!sessionStorage.getItem(PART_KEY));

  const load = () => pApi.get("/me").then((r) => setData(r.data)).catch(() => sessionStorage.removeItem(PART_KEY)).finally(() => setChecking(false));
  useEffect(() => { if (sessionStorage.getItem(PART_KEY)) load(); }, []);

  const doLogin = async () => {
    setBusy(true);
    try {
      const r = await pApi.post("/participant/login", login);
      sessionStorage.setItem(PART_KEY, r.data.token);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const sendCorr = async () => {
    try {
      await pApi.post("/me/corrections", { changes: { [corr.field]: corr.value }, reason: corr.reason, attendance_id: corr.attendance_id || null });
      toast.success("Correction request sent to the Parish Administrator");
      setCorr({ field: "", value: "", reason: "", attendance_id: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const changePin = async () => {
    try { await pApi.post("/me/pin", pins); toast.success("PIN changed"); setPins({ current_pin: "", new_pin: "" }); } catch (e) { toast.error(errMsg(e)); }
  };
  const out = () => { sessionStorage.removeItem(PART_KEY); setData(null); };
  const p = data?.profile;

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[17px]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-8 py-4 border-b border-[#E5DEC9]">
        <Link to="/"><ParishMark small /></Link><LangToggle />
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8 grid gap-5">
        <h1 className="font-serif text-3xl font-bold text-[#2D3B2D]">Participant Profile</h1>
        {checking && <Loading />}
        {!checking && !p && (
          <Section title={t("verify")}>
            <div className="grid gap-4">
              <Field label={t("code")}><input className={`${inputCls} h-14 text-lg font-mono uppercase`} value={login.code} onChange={(e) => setLogin({ ...login, code: e.target.value })} data-testid="profile-code" /></Field>
              <Field label={t("pinShort")}><input className={`${inputCls} h-14 text-2xl tracking-[0.5em] font-mono`} type="password" inputMode="numeric" maxLength={4} value={login.pin} onChange={(e) => setLogin({ ...login, pin: e.target.value.replace(/\D/g, "") })} data-testid="profile-pin" /></Field>
              <button className="btn-big btn-primary" disabled={busy} onClick={doLogin} data-testid="profile-login-btn">{t("verify")}</button>
              <p className="text-sm text-stone-600">{t("forgot")}</p>
            </div>
          </Section>
        )}
        {p && (
          <>
            <div className="sfap-card p-6" data-testid="profile-card">
              <div className="flex justify-between gap-3"><span className="font-mono text-[#5C3A21] font-bold">{p.code}</span><button className="text-sm underline" onClick={out} data-testid="profile-logout">Sign out</button></div>
              <p className="font-serif text-2xl font-bold mt-2">{p.display_name}</p>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm text-stone-700">
                {[["Type", p.type === "parish" ? "Parishioner" : "External participant"], ["Organization", p.organization], ["Address", [p.address, p.barangay, p.city].filter(Boolean).join(", ")], ["Mobile", p.mobile], ["Email", p.email], ["Facebook", p.facebook]].filter((x) => x[1]).map(([k, v]) => <div key={k}><span className="text-stone-500">{k}: </span>{v}</div>)}
              </div>
              {data.open_meeting_ids.length > 0 && (
                <div className="mt-4 rounded-xl bg-green-50 border border-green-200 p-3 text-sm">Registration is open for a meeting you attend. You may correct your details directly: {data.open_meeting_ids.map((id) => <Link key={id} className="underline font-semibold ml-1" to={`/m/${id}`}>open meeting</Link>)}</div>
              )}
            </div>
            <Section title="My Participant ID card">
              <IdCardBlock code={p.code} name={p.display_name} line={p.type === "external" ? p.organization : [p.barangay, p.city].filter(Boolean).join(", ")} type={p.type} />
            </Section>
            <Section title="My attendance history">
              <div className="divide-y divide-[#F0EADC]" data-testid="profile-history">
                {data.history.map((h) => (
                  <div key={h.id} className="py-3 flex flex-wrap justify-between gap-2">
                    <div><div className="font-semibold">{h.meeting?.title}</div><div className="text-xs text-stone-500">{h.meeting?.date} · {h.snap.position} {h.snap.ministry_name ? `· ${h.snap.ministry_name}` : ""} · check-in {fmtTime(h.checkin_at)}</div></div>
                    <StatusBadge status={h.status} />
                  </div>
                ))}
                {!data.history.length && <p className="text-stone-500 text-sm">No attendance yet.</p>}
              </div>
            </Section>
            <Section title="Request a correction">
              <p className="text-sm text-stone-600 mb-3">Outside the registration period, corrections are reviewed and approved by the Parish Administrator.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="What needs correction?">
                  <select className={inputCls} value={corr.field} onChange={(e) => setCorr({ ...corr, field: e.target.value })} data-testid="corr-field">
                    <option value="">Select</option>
                    {(p.type === "parish" ? ["first_name", "middle_name", "last_name", "suffix", "address", "barangay", "city", "mobile", "email", "facebook", "position"] : ["full_name", "title", "organization", "designation", "diocese", "city", "mobile", "email"]).map((f) => <option key={f} value={f}>{f.replace("_", " ")}</option>)}
                  </select>
                </Field>
                <Field label="Correct value"><input className={inputCls} value={corr.value} onChange={(e) => setCorr({ ...corr, value: e.target.value })} data-testid="corr-value" /></Field>
                <Field label="For a specific meeting (optional)">
                  <select className={inputCls} value={corr.attendance_id} onChange={(e) => setCorr({ ...corr, attendance_id: e.target.value })} data-testid="corr-attendance">
                    <option value="">Profile only</option>{data.history.map((h) => <option key={h.id} value={h.id}>{h.meeting?.title}</option>)}
                  </select>
                </Field>
                <Field label="Reason"><input className={inputCls} value={corr.reason} onChange={(e) => setCorr({ ...corr, reason: e.target.value })} data-testid="corr-reason" /></Field>
              </div>
              <button className="btn-big btn-primary mt-4" disabled={!corr.field || !corr.value || !corr.reason} onClick={sendCorr} data-testid="corr-submit">Send request</button>
              {data.corrections.map((c) => <div key={c.id} className="text-sm mt-2 text-stone-600">{Object.entries(c.changes).map(([k, v]) => `${k} → ${v}`).join(", ")} · <b>{c.status}</b></div>)}
            </Section>
            <Section title="Change PIN">
              <div className="flex flex-wrap gap-3">
                <input className={`${inputCls} max-w-[160px] font-mono`} placeholder="Current" type="password" inputMode="numeric" maxLength={4} value={pins.current_pin} onChange={(e) => setPins({ ...pins, current_pin: e.target.value })} data-testid="pin-current" />
                <input className={`${inputCls} max-w-[160px] font-mono`} placeholder="New" type="password" inputMode="numeric" maxLength={4} value={pins.new_pin} onChange={(e) => setPins({ ...pins, new_pin: e.target.value })} data-testid="pin-new" />
                <button className="btn-big btn-ghost" onClick={changePin} data-testid="pin-change-btn">Change PIN</button>
              </div>
            </Section>
          </>
        )}
      </main>
    </div>
  );
}
